import { Component, HostBinding, Input } from '@angular/core';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  NgxMatDatetimepicker,
  NgxMatDatepickerToggle,
  NgxMatDatepickerInput,
  NgxMatDatepickerActions,
  NgxMatDatepickerApply,
  NgxMatDatepickerCancel
} from '@ngxmc/datetime-picker';
import { TranslatePipe } from '@ngx-translate/core';
import { EnfafIconComponent } from '../enfaf-icon/enfaf-icon.component';

@Component({
  selector: 'enfaf-datetime-picker-light',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    NgxMatDatetimepicker,
    NgxMatDatepickerToggle,
    NgxMatDatepickerInput,
    NgxMatDatepickerActions,
    NgxMatDatepickerApply,
    NgxMatDatepickerCancel,
    EnfafIconComponent,
    TranslatePipe
  ],
  templateUrl: './enfaf-datetime-picker-light.component.html',
  styleUrls: ['./enfaf-datetime-picker-light.component.scss']
})
export class EnfafDatetimePickerLightComponent {
  @Input() control!: FormControl<Date | null>;
  @Input() description?: string;
  @Input() errorLabel: string = '';
  @Input() autocomplete: string = 'off';
  @Input() name: string = '';
  @Input() label: string = 'Esto es label';
  @Input() placeholder: string = 'Esto es placeholder';
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  @Input() disabled = false;
  @Input() showSpinners = true;
  @Input() showSeconds = false;
  @Input() stepHour = 1;
  @Input() stepMinute = 1;
  @Input() stepSecond = 1;
  @Input() touchUi = false;
  @Input() enableMeridian = false;
  @Input() disableMinute = false;
  @Input() hideTime = false;
  /**
   * `dark` (default, original look): white text on a blue tint, meant for dark backgrounds.
   * `light`: for white modals/cards (dark text on a white field, WCAG AA contrast).
   */
  @Input() variant: 'dark' | 'light' = 'dark';

  @HostBinding('class.datetime-picker--light')
  get isLightVariant(): boolean {
    return this.variant === 'light';
  }

  get hasOtherErrors() {
    return this.control.hasError('invalidAge');
  }

  get hasValue() {
    return this.control && this.control.value !== null;
  }

  private parseManualInput(input: string): Date | null {
    if (!input || typeof input !== 'string') return null;

    // Remove extra spaces and normalize
    const cleanInput = input.trim().replace(/,/g, ''); // Remove commas

    // Try different date formats - more flexible patterns
    const formats = [
      // DD/M/YYYY HH:mm:ss (31/7/2025 09:39:53)
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{1,2}):(\d{1,2})$/,
      // DD/M/YYYY HH:mm (31/7/2025 09:39)
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{1,2})$/,
      // DD/M/YYYY (31/7/2025)
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
      // YYYY-MM-DD HH:mm:ss
      /^(\d{4})-(\d{1,2})-(\d{1,2})\s+(\d{1,2}):(\d{1,2}):(\d{1,2})$/,
      // YYYY-MM-DD HH:mm
      /^(\d{4})-(\d{1,2})-(\d{1,2})\s+(\d{1,2}):(\d{1,2})$/,
      // YYYY-MM-DD
      /^(\d{4})-(\d{1,2})-(\d{1,2})$/,
      // DD-MM-YYYY HH:mm:ss
      /^(\d{1,2})-(\d{1,2})-(\d{4})\s+(\d{1,2}):(\d{1,2}):(\d{1,2})$/,
      // DD-MM-YYYY HH:mm
      /^(\d{1,2})-(\d{1,2})-(\d{4})\s+(\d{1,2}):(\d{1,2})$/,
      // DD-MM-YYYY
      /^(\d{1,2})-(\d{1,2})-(\d{4})$/
    ];

    for (let i = 0; i < formats.length; i++) {
      const format = formats[i];
      const match = cleanInput.match(format);
      if (match) {
        let year: number,
          month: number,
          day: number,
          hour = 0,
          minute = 0,
          second = 0;

        // Check if it's a YYYY-first format
        if (i >= 3 && i <= 5) {
          // YYYY-MM-DD format
          if (match.length >= 7) {
            [, year, month, day, hour = 0, minute = 0, second = 0] = match.map(Number);
          } else if (match.length >= 5) {
            [, year, month, day, hour = 0, minute = 0] = match.map(Number);
          } else {
            [, year, month, day] = match.map(Number);
          }
        } else {
          // DD/MM/YYYY or DD-MM-YYYY format
          if (match.length >= 7) {
            [, day, month, year, hour = 0, minute = 0, second = 0] = match.map(Number);
          } else if (match.length >= 5) {
            [, day, month, year, hour = 0, minute = 0] = match.map(Number);
          } else {
            [, day, month, year] = match.map(Number);
          }
        }

        // Validate ranges
        if (month < 1 || month > 12) continue;
        if (day < 1 || day > 31) continue;
        if (hour < 0 || hour > 23) continue;
        if (minute < 0 || minute > 59) continue;
        if (second < 0 || second > 59) continue;

        const date = new Date(year, month - 1, day, hour, minute, second);

        // Always set seconds to 0 regardless of input
        date.setSeconds(0);
        date.setMilliseconds(0);

        // Verify the date is valid (handles things like Feb 30)
        if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
          return date;
        }
      }
    }

    // Try native Date parsing as fallback
    const nativeDate = new Date(cleanInput);
    if (!isNaN(nativeDate.getTime())) {
      // Also normalize seconds for native parsing
      nativeDate.setSeconds(0);
      nativeDate.setMilliseconds(0);
      return nativeDate;
    }

    return null;
  }

  /**
   * Optional replacement for the built-in parsing of typed text (which builds a `Date` in the browser's zone).
   * Whatever it returns (non-null) becomes the control value; `null` leaves the value the picker already parsed.
   * Used by the course purchase validity to read typed times directly in Madrid (ENF-2372 QA-A-03).
   */
  @Input() manualInputParser: ((text: string) => unknown) | null = null;

  onInputBlur(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.value && this.control && this.manualInputParser) {
      const parsedValue = this.manualInputParser(input.value);
      if (parsedValue !== null && parsedValue !== undefined) {
        (this.control as FormControl<unknown>).setValue(parsedValue);
        this.control.markAsTouched();
      }
      return;
    }
    if (input.value && this.control) {
      const parsedDate = this.parseManualInput(input.value);
      if (parsedDate) {
        // Ensure seconds are always 0
        parsedDate.setSeconds(0);
        parsedDate.setMilliseconds(0);
        this.control.setValue(parsedDate);
        this.control.markAsTouched();
      }
    }
  }
}
